import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import classNames from 'classnames';
import {
  Listing,
  showSensitiveContent,
  TEASER_VERIFICATION,
} from 'models/Listing';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from 'api';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import AuthContext from 'providers/Auth/Auth.context';
import useScrollLock from 'hooks/useScrollLock';
import { Button, ImageSlider } from 'ncoded-component-library';
import ResolveImage from 'components/ResolveImage';
import StarIcon from 'icons/Star.icon';
import HighlightsIcon from 'icons/Highlights.icon';
import ProductsIcon from 'icons/Products.icon';
import KeyAssetsIcon from 'icons/KeyAssets.icon';
import ResolvePopup from 'components/ResolvePopup';
import { getImgUrl } from 'components/ResolveImage/utils';
import LinkToIcon from 'icons/LinkTo.icon';
import utils, {
  checkIsPrivate,
  checkIsPublic,
  checkIsUrgent,
  getAcceptedListingContacts,
  getBuyingPortfolio,
  getIsListingOwner,
  getPrivateListingRoute,
  getResolveUrl,
  getSellingPortfolio,
  sentryError,
} from 'utils';
import EoiDate from 'components/EoiDate';
import ChatIcon from 'icons/Chat.icon';
import ShareButton from 'components/ShareButton';
import ListingPrices from 'components/ListingPrices';
import LockListingImage from 'assets/images/logo-locked.webp';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import { APPOINTMENT_TYPE_COPIES } from 'components/DistressedListingCard/utils';
import useSiteInitializedState from 'hooks/useSiteInitializedState';
import AdvertisingBusinessCard from 'components/AdvertisingBusinessCard';
import LockclosedIcon from 'icons/Lockclosed.icon';
import XcircleIcon from 'icons/Xcircle.icon';
import useLocationMapper, {
  LOCATION_URL_MAPPING,
} from 'router/data/useLocationMapper';
import { uuid4 } from '@sentry/utils';
import BusinessDetailsBadge from '../../components/BusinessDetailsBadge';
import {
  ImagePlaceholder,
  MainPlaceholder,
  ContactInformationPlaceholder,
} from './placeholders';
import { BUSINESS_ROUTE } from 'data/global';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import { AxiosError } from 'axios';
import HighlightsSection from '../../components/HighlightsSection';
import SimilarListings from '../../components/SimilarListings';
import useSponsoredListings from 'router/subrouters/Dash/subrouters/hooks/useSponsoredListings';
import useBusiness from './hooks/useBusiness';
import useOpenEnquire from 'hooks/useOpenEnquire';
import useWatchlist from 'hooks/useWatchlist';

import './Business.styles.scss';
import './Business.styles.responsive.scss';

type BusinessProps = {
  className?: string;
};

const isNotEmpty = (array: Array<string> | undefined) =>
  array && !!array.length;

const Business: React.FC<BusinessProps> = (props) => {
  const { className } = props;

  const { listingSlug } =
    useParams<{ listingSlug: string; listingCategory: string }>();

  const [listing, setListing] = useSiteInitializedState<Listing>(
    `listing-${listingSlug}`,
  );

  const [isLoading, setIsLoading] = useState(!listing);

  const { isLoggedIn } = useContext(AuthContext);
  const { currentUser } = useContext(CurrentUserContext);
  const { popServerError } = useContext(PopNotificationsContext);

  const isListingOwner = getIsListingOwner(
    currentUser?._id,
    listing?.userId,
    listing?.isCoOwner,
  );

  const listingId = listing?._id || '';
  const dealRoomId = listing?.dealRoomId || '';
  const isWatchlisted = !!listing?.isWatchlisted;

  const needsToBeLoggedIn =
    listing?.teaserVerification ===
      TEASER_VERIFICATION.WITH_EMAIL_VERIFICATION && !isLoggedIn;

  const isEnabledTeaser = listing?.enableTeaser && !needsToBeLoggedIn;

  const classes = classNames('business', className);

  const { locationLabelMapper } = useLocationMapper();

  const navigate = useNavigate();

  const { t } = useTranslation();

  const { unlockScroll } = useScrollLock();

  const { HelmetContent, HeadingNavigation } = useBusiness(listing);

  const { rightRailAd } = useSponsoredListings(listingId);

  const { showEnquire, showWatchlist } = useShowEnquireAndWatchlist(listing);

  const isSensitiveContentShown = useMemo(
    () => showSensitiveContent(listing),
    [listing],
  );

  const openEnquire = useOpenEnquire();

  const RenderShareButton = useMemo(
    () =>
      checkIsPublic(listing?.stage) ? (
        <ShareButton
          url={getResolveUrl(
            utils.composeBusinessUrl(
              listing.industry,
              listing.slugName,
              listing.type,
            ),
          )}
        />
      ) : null,
    [listing],
  );

  const RenderMessageBroker = useMemo(() => {
    const { sessionId, listingEnquiryId } = listing || {};

    return (
      sessionId &&
      listingEnquiryId && (
        <Link
          className="ncoded-button ncoded-button--solid"
          to={`${getBuyingPortfolio(
            listingEnquiryId,
            'messaging',
          )}/${sessionId}`}
        >
          <ChatIcon />
          {t('message')}
        </Link>
      )
    );
  }, [listing, t]);

  const handleNotInterested = useCallback(async () => {
    try {
      await api.listings.declineInterest(listingId);
      navigate(BUSINESS_ROUTE, { replace: true });
    } catch (error) {
      sentryError(error);
    }
  }, [listingId, navigate]);

  // unlock scroll if user reroutes while slider open:
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => unlockScroll(), []);

  // fetch listing:
  useEffect(() => {
    !utils.isSSGHuman() &&
      listingSlug &&
      (async () => {
        // In case
        setListing((old) => {
          if (!old) setIsLoading(true);
          return old;
        });
        try {
          const {
            data: { listing },
          } = await api.listings.getListingWithSlug(listingSlug, {
            $relations: '["dealRoom"]',
          });

          setListing(listing);
        } catch (e) {
          const error = e as AxiosError & { listingId?: string };

          const listingId = error?.response?.data?.error?.listingId;

          if (listingId) {
            navigate(getPrivateListingRoute(listingId), {
              replace: true,
            });
          } else {
            navigate('/page-not-found', { replace: true });
          }
        } finally {
          setIsLoading(false);
        }
      })();
  }, [listingSlug, navigate, popServerError, setListing]);

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    dealRoomId,
    isWatchlisted,
    (isWatchlisted) => setListing((old) => ({ ...old, isWatchlisted })),
  );

  const EnquireButton = useMemo(
    () => (
      <Button
        icon={ChatIcon}
        onClick={() => openEnquire(listingId, isEnabledTeaser)}
      >
        {t('enquire')}
      </Button>
    ),
    [isEnabledTeaser, listingId, openEnquire, t],
  );

  const RenderSellerInfoActions = useMemo(() => {
    const { isWatchlisted } = listing || {};

    return (
      <div className="resolve-agent-details__actions">
        {showEnquire && EnquireButton}
        {RenderMessageBroker}
        {showWatchlist && (
          <ResolvePopup
            onHover
            popupContent={t(
              isListingOwner
                ? 'thisIsYourListing'
                : isLoggedIn
                ? isWatchlisted
                  ? 'removeWatchlistedListing'
                  : 'watchlistListing'
                : 'loginToWatchlist',
            )}
          >
            <Button
              className={classNames('watchlist-button', {
                'watch-listed': isWatchlisted,
              })}
              icon={<StarIcon />}
              disabled={isListingOwner || watchlistRequestLoading}
              onClick={onWatchlist}
              variant="link"
            >
              {t('shortlist')}
            </Button>
          </ResolvePopup>
        )}
        {RenderShareButton}
      </div>
    );
  }, [
    listing,
    showEnquire,
    EnquireButton,
    RenderMessageBroker,
    showWatchlist,
    isListingOwner,
    isLoggedIn,
    watchlistRequestLoading,
    RenderShareButton,
    t,
    onWatchlist,
  ]);

  const RenderExpiredEmptyState = useMemo(
    () => (
      <div className="resolve-agent-details">
        <p className="empty-title">{t('lookingToBuyBusiness')}</p>
        <Link to={BUSINESS_ROUTE}>{t('browseOurList')}</Link>
      </div>
    ),
    [t],
  );

  const RenderPresentedBy = useMemo(() => {
    const { appointmentType, type } = listing || {};

    if (!checkIsUrgent(type))
      return <p className="title">{t('presentedBy')}</p>;

    return (
      <p className="title">
        {t(
          (APPOINTMENT_TYPE_COPIES[
            appointmentType as keyof typeof APPOINTMENT_TYPE_COPIES
          ] as string) || APPOINTMENT_TYPE_COPIES.Other,
        )}
      </p>
    );
  }, [listing, t]);

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listing?.listingContacts),
    [listing?.listingContacts],
  );

  const RenderAgentDetails = useMemo(
    () =>
      acceptedListingContacts.length ? (
        <div className="resolve-agent-details">
          {RenderPresentedBy}

          <div className="resolve-agent-details__image-wrapper">
            {listing.listingContact?.firmLogo?.url && (
              <ResolveImage
                asCompanyLogo
                src={getImgUrl(listing.listingContact?.firmLogo?.url, '480')}
              />
            )}
          </div>

          <ul>
            {acceptedListingContacts.map(({ name, position, brokerImage }) => (
              <li key={uuid4()}>
                <div className="res-lc-content">
                  <span>{name}</span>
                  <span>{position}</span>
                </div>
                {brokerImage?.url && <ResolveImage src={brokerImage.url} />}
              </li>
            ))}
          </ul>
          {RenderSellerInfoActions}
        </div>
      ) : (
        // details empty state
        <div className="resolve-agent-details">
          <div className="resolve-agent-details__image-wrapper">
            <ResolveImage src={LockListingImage} asCompanyLogo />
          </div>
          <p className="empty-title">{t('lockEnquireTitle')}</p>
          <p className="empty-description">{t('lockEnquireDescription')}</p>
          {RenderSellerInfoActions}
        </div>
      ),
    [
      RenderPresentedBy,
      RenderSellerInfoActions,
      listing,
      acceptedListingContacts,
      t,
    ],
  );

  const RenderHighlightsSection = useMemo(() => {
    const { highlights, products, keyAssets } = listing || {};

    return (
      listing &&
      isSensitiveContentShown && (
        <div className="business__main__highlights">
          {isNotEmpty(highlights) && (
            <HighlightsSection
              titleLabel="highlights"
              listArray={highlights}
              icon={HighlightsIcon}
            />
          )}

          {isNotEmpty(products) && (
            <HighlightsSection
              titleLabel="products"
              listArray={products}
              icon={ProductsIcon}
            />
          )}

          {isNotEmpty(keyAssets) && (
            <HighlightsSection
              titleLabel="keyAssets"
              listArray={keyAssets}
              icon={KeyAssetsIcon}
            />
          )}
        </div>
      )
    );
  }, [isSensitiveContentShown, listing]);

  const ImageContent = useMemo(
    () =>
      isLoading ? (
        <div className="res-ldp-image-container">
          <ImagePlaceholder />
        </div>
      ) : (
        listing && (
          <div className="res-ldp-image-container">
            {isSensitiveContentShown ? (
              listing.gallery?.length > 1 ? (
                <ImageSlider
                  slides={listing.gallery.map(({ url }) => ({
                    imageUrl: getImgUrl(url, '1024'),
                  }))}
                  showDots={!!listing.gallery?.length}
                  className="listing-card-resolve__image-slider-container__slider"
                />
              ) : (
                <ResolveImage
                  className="listing-card-image"
                  src={getImgUrl(listing.gallery?.[0]?.url, '1024')}
                />
              )
            ) : (
              <ResolveImage className="listing-card-image" />
            )}
          </div>
        )
      ),
    [isLoading, isSensitiveContentShown, listing],
  );

  const MainCardContent = useMemo(
    () =>
      isLoading ? (
        <MainPlaceholder />
      ) : (
        listing && (
          <div className="business__main">
            <h1>{listing.listingTitle}</h1>
            <div className="business__main__links">
              {listing?.location?.state ? (
                <Link
                  to={`${BUSINESS_ROUTE}/${utils.createSlug(
                    LOCATION_URL_MAPPING[listing.location.state],
                  )}`}
                >
                  {locationLabelMapper[listing.location.state]}
                </Link>
              ) : listing?.locationPostcode ? (
                <span className="postcode">
                  <LocationmarkerIcon />
                  {listing?.locationPostcode}
                </span>
              ) : (
                listing?.dealLocation
              )}
              <Link
                to={`${BUSINESS_ROUTE}/${utils.createSlug(listing.industry)}`}
              >
                {listing.industry}
              </Link>
            </div>
            <EoiDate
              bigger
              seekingExpressionOfInterestDate={
                listing.seekingExpressionOfInterestDate
              }
            />
            {isSensitiveContentShown && (
              <ListingPrices
                askingPrice={listing?.askingPriceSelect}
                amount={listing?.amount}
                amountFrom={listing?.amountFrom}
                amountTo={listing?.amountTo}
                revenue={listing?.revenue}
                EBITDA={listing?.EBITDA}
                averageStockValue={listing?.averageStockValue}
                stockAtValue={listing?.stockAtValue}
                currency={listing?.currency}
              />
            )}
            <p className="business__main__description">
              {listing.businessDescription}
            </p>
            <div className="business__main__actions">
              {showEnquire && EnquireButton}
              {isSensitiveContentShown && (
                <>
                  {isListingOwner && (
                    <Link
                      className="ncoded-button ncoded-button--primary ncoded-button--outline"
                      to={getSellingPortfolio(listing.dealRoomId)}
                    >
                      <LinkToIcon />
                      {t('viewInMyPortfolio')}
                    </Link>
                  )}
                  {showWatchlist && (
                    <ResolvePopup
                      popupContent={t(
                        isListingOwner
                          ? 'thisIsYourListing'
                          : isLoggedIn
                          ? listing.isWatchlisted
                            ? 'removeWatchlistedListing'
                            : 'watchlistListing'
                          : 'loginToWatchlist',
                      )}
                      onHover
                    >
                      <Button
                        className={classNames('watchlist-button', {
                          'watch-listed': listing.isWatchlisted,
                        })}
                        variant="link"
                        icon={<StarIcon />}
                        disabled={isListingOwner || watchlistRequestLoading}
                        onClick={onWatchlist}
                      >
                        {t('shortlist')}
                      </Button>
                    </ResolvePopup>
                  )}
                </>
              )}
              {RenderShareButton}
            </div>
            {RenderHighlightsSection}
          </div>
        )
      ),
    [
      isLoading,
      listing,
      locationLabelMapper,
      isSensitiveContentShown,
      showEnquire,
      EnquireButton,
      isListingOwner,
      showWatchlist,
      isLoggedIn,
      watchlistRequestLoading,
      RenderShareButton,
      RenderHighlightsSection,
      t,
      onWatchlist,
    ],
  );

  return (
    <div className={classes}>
      {HelmetContent}
      {HeadingNavigation}

      <div className="business__heading">
        {isLoading ? null : (
          <>
            {checkIsPrivate(listing?.stage) && listing?.isPrivateViewer && (
              <div className="business__heading__private-listing">
                <LockclosedIcon />
                <div>
                  <p>{t('privateListingBannerTitle')}</p>
                  <p>{t('privateListingBannerDescription')}</p>
                </div>
                {showEnquire && EnquireButton}
                <Button
                  variant="outline"
                  icon={XcircleIcon}
                  onClick={handleNotInterested}
                >
                  {t('notInterested')}
                </Button>
              </div>
            )}
            <BusinessDetailsBadge
              stage={listing.stage}
              dealStage={listing.dealRoom?.stage}
            />
          </>
        )}
      </div>

      {ImageContent}

      {MainCardContent}

      <aside>
        <div>
          {isLoading ? (
            <ContactInformationPlaceholder />
          ) : isSensitiveContentShown ? (
            RenderAgentDetails
          ) : (
            RenderExpiredEmptyState
          )}
          {rightRailAd && (
            <AdvertisingBusinessCard
              title={rightRailAd.title}
              description={rightRailAd.description}
              coverPhoto={rightRailAd.coverPhoto}
              logoImage={rightRailAd.logoImage}
              link={rightRailAd.link}
              ctaText={rightRailAd.ctaText}
              onlyMobileView
            />
          )}
        </div>
      </aside>

      <SimilarListings listingId={listingId} isLoading={isLoading} />
    </div>
  );
};

export default Business;
